import React from 'react';
import { injectIntl, type WrappedComponentProps } from 'react-intl';
import { IconButton } from '@eventbrite/legacy-eds.icon-button';
import { LogoMessenger } from '@eventbrite/legacy-eds.iconography';
import { FACEBOOK_MESSENGER_AFFILIATED_CODE } from './constants';
import { getSdk, initApplication, openUI, openUrl } from './utils';
import type { UtmOptions } from './types';

interface FacebookOptions {
    appId: string;
    locale: string;
    page: string;
    version: string;
    xfbml: boolean;
}

interface FacebookMessengerShareProps {
    resourceId: string;
    resourceName: string;
    resourceUrl: string;
    resourceType: string;
    facebookOptions: FacebookOptions;
    isMobile?: boolean;
    onClick?: () => void;
    utmOptions?: UtmOptions;
    affiliateChannel?: string;
    dataHeapId?: string;
    shareMethod?: string;
    dataPageArea?: string;
    className?: string;
}

const _getResourceUrl = (
    resourceUrl: string,
    affiliateChannel?: string,
    utmOptions?: UtmOptions,
) => {
    const url = new URL(resourceUrl);
    const aff = FACEBOOK_MESSENGER_AFFILIATED_CODE[affiliateChannel || 'default'];
    url.searchParams.set('aff', aff);

    if (utmOptions) {
        Object.entries(utmOptions).forEach(([key, value]) => {
            if (value) {
                url.searchParams.set(key, value);
            }
        });
    }

    return url.toString();
};

const _getFacebookUrl = (
    resourceUrl: string,
    appId: string,
    affiliateChannel?: string,
    utmOptions?: UtmOptions,
) => {
    const url = new URL('fb-messenger://share');
    url.searchParams.set('link', _getResourceUrl(resourceUrl, affiliateChannel, utmOptions));
    url.searchParams.set('app_id', appId);
    return url.toString();
};

class FacebookMessengerShareComponent extends React.Component<
    FacebookMessengerShareProps & WrappedComponentProps<'intl'>
> {
    componentDidMount() {
        const { locale, ...options } = this.props.facebookOptions;

        // get facebook-sdk as soon as the component is mounted
        getSdk(locale).then(() => initApplication(options));
    }

    _handleButtonClick() {
        const {
            affiliateChannel,
            resourceName,
            resourceUrl,
            facebookOptions: { appId, locale },
            intl,
            isMobile,
            onClick,
            utmOptions,
        } = this.props;

        if (onClick) {
            onClick();
        }

        if (isMobile) {
            openUrl(_getFacebookUrl(resourceUrl, appId, affiliateChannel, utmOptions));
        } else {
            const description = intl.formatMessage(
                {
                    id: 'Check out "{resourceName}" @Eventbrite',
                    defaultMessage: 'Check out "{resourceName}" @Eventbrite',
                },
                {
                    resourceName,
                },
            );
            const options = {
                description,
                method: 'send',
                display: 'popup',
                link: _getResourceUrl(resourceUrl, affiliateChannel, utmOptions),
            };

            // verify facebook-sdk is loaded and then open facebook share dialog
            getSdk(locale).then(() => openUI(options));
        }
    }

    render() {
        const { intl } = this.props;
        return (
            <span
                className={this.props.className}
                data-testid="facebook-messenger-share-box-container"
                data-heap-id={this.props.dataHeapId}
                data-share-method={this.props.shareMethod}
                data-page-area={this.props.dataPageArea}
                {...{
                    [`data-${this.props.resourceType}-id`]: this.props.resourceId,
                }}
            >
                <IconButton
                    iconType={<LogoMessenger />}
                    title={intl.formatMessage({
                        id: 'Share on Facebook Messenger',
                        defaultMessage: 'Share on Facebook Messenger',
                    })}
                    onClick={this._handleButtonClick.bind(this)}
                />
            </span>
        );
    }
}

export const FacebookMessengerShare = injectIntl(FacebookMessengerShareComponent);
